﻿import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AlertTriangle, MessageCircle, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGrowthData } from "@/hooks/growth/useGrowthData";

export function ChurnPanel({
  data,
}: {
  data: ReturnType<typeof useGrowthData>;
}) {
  const { churnData } = data;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-rose-500/10 text-rose-500 rounded-xl">
          <AlertTriangle size={24} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-100">
            Alerta de Churn (Risco de Perda)
          </h2>
          <p className="text-sm text-slate-400">
            Clientes que não retornam há mais de 45 dias. Envie uma mensagem
            para atraí-los novamente.
          </p>
        </div>
      </div>

      {churnData.length === 0 ? (
        <div className="p-12 text-center border border-slate-800 border-dashed rounded-xl">
          <Users className="mx-auto text-emerald-500 mb-3" size={40} />
          <h3 className="text-lg font-bold text-slate-300">
            Retenção Excelente!
          </h3>
          <p className="text-slate-500">
            Você não possui clientes inativos há mais de 45 dias.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-950 text-slate-400">
              <tr>
                <th className="p-4 rounded-tl-lg font-semibold">Cliente</th>
                <th className="p-4 font-semibold">Última Visita</th>
                <th className="p-4 font-semibold text-rose-400">
                  Tempo Afastado
                </th>
                <th className="p-4 rounded-tr-lg font-semibold text-right">
                  Ação Recomendada (IA)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50 text-slate-300">
              {churnData.map((c) => {
                let suggestion = "";
                let discount = "";
                if (c.daysAway >= 90) {
                  suggestion = "Risco Alto. Ofereça 20% OFF.";
                  discount = "20% OFF em qualquer lavagem";
                } else {
                  suggestion = "Risco Médio. Ofereça 10% OFF.";
                  discount = "10% de desconto na sua próxima visita";
                }

                const text = `Olá ${c.name}! Notamos que faz um tempo desde sua última visita ao nosso Lava Jato. Para te ver novamente, estamos te dando ${discount}! Vamos agendar?`;

                return (
                  <tr
                    key={c.id}
                    className="hover:bg-slate-800/30 transition-colors"
                  >
                    <td className="p-4 font-medium text-slate-200">
                      {c.name}
                      <br />
                      <span className="text-xs text-slate-500 font-normal">
                        {c.phone || "Sem número"}
                      </span>
                    </td>
                    <td className="p-4 text-slate-400">
                      {format(c.lastVisit, "dd 'de' MMM, yyyy", {
                        locale: ptBR,
                      })}
                    </td>
                    <td className="p-4 font-bold text-rose-400">
                      {c.daysAway} dias
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex flex-col items-end gap-2">
                        <span className="text-xs text-slate-500">
                          {suggestion}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-[#25D366]/10 text-[#25D366] border-[#25D366]/30 hover:bg-[#25D366] hover:text-white"
                          onClick={() => {
                            if (c.phone) {
                              const phoneNum = c.phone.replace(/\D/g, "");
                              window.open(
                                `https://wa.me/55${phoneNum}?text=${encodeURIComponent(text)}`,
                                "_blank",
                              );
                            } else {
                              alert("Cliente sem telefone cadastrado.");
                            }
                          }}
                        >
                          <MessageCircle size={14} className="mr-2" />
                          Enviar Promoção
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
